import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Shield, Users, Clock, Award, Scale, TrendingDown } from "lucide-react";
import { useRef, useEffect, useCallback } from "react";
import legitScriptBadge from "@/assets/legitscript-certified.png";
import orderlyMedsLogo from "@/assets/orderly-meds-logo.png";
import { useCounterAnimation } from "@/hooks/use-counter-animation";

// Import static hero image
import heroImage from "@/assets/hero-image-new.jpg";

const trustStats = [
  { value: 57, suffix: "k+", label: "Weight Loss Results", icon: Award },
  { value: 1, suffix: "m+", label: "Pounds Lost", icon: Scale },
  { value: 15, suffix: " lbs", label: "Avg loss on 3-mo plan", icon: TrendingDown },
];

const AnimatedStat = ({ stat, delay }: { stat: typeof trustStats[0]; delay: number }) => {
  const { count, elementRef } = useCounterAnimation({ 
    end: stat.value, 
    duration: 2000,
    delay 
  });
  
  return (
    <div 
      ref={elementRef}
      className="group flex flex-col items-center gap-1.5 sm:gap-2 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
    >
      <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#ffdb4d] group-hover:scale-110 transition-transform" />
      <span className="text-lg sm:text-xl md:text-2xl font-black text-white">{count}{stat.suffix}</span>
      <span className="text-[10px] sm:text-xs text-white/60 text-center leading-tight">{stat.label}</span>
    </div>
  );
};

interface TrueWellnessHeroProps {
  onOpenModal: () => void;
  currentIndex: number;
  isTransitioning: boolean;
  setCurrentIndex: (index: number) => void;
  setIsTransitioning: (transitioning: boolean) => void;
}

const TrueWellnessHero = ({ onOpenModal }: TrueWellnessHeroProps) => {
  const sectionRef = useRef<HTMLElement>(null);

  // Parallax effect - disabled on mobile for smooth scrolling
  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    // Disable parallax on mobile/tablet for smoother performance
    if (window.innerWidth < 1024) return;
    
    const scrolled = window.scrollY;
    const sectionHeight = sectionRef.current.offsetHeight;
    const image = sectionRef.current.querySelector('.hero-bg-image') as HTMLElement;
    if (scrolled <= sectionHeight && image) {
      image.style.transform = `translateY(${scrolled * 0.3}px) scale(1.05)`;
    }
  }, []);

  useEffect(() => {
    // Only add scroll listener on desktop
    if (window.innerWidth >= 1024) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <section 
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center pt-20 md:pt-24 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Static Hero Background with Ken Burns effect */}
      <div className="absolute inset-0 bg-[#0a0f14]">
        <img
          src={heroImage}
          alt=""
          width={1920}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="sync"
          className="hero-bg-image absolute inset-0 w-full h-full object-cover object-center"
        />
      </div>
      
      {/* Premium cinematic overlay system */}
      {/* Base dark overlay for contrast */}
      <div className="absolute inset-0 bg-black/30" />
      
      {/* Cinematic gradient - darker at edges, lighter in center */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/60" />
      
      {/* Radial vignette for cinematic depth */}
      <div 
        className="absolute inset-0" 
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, transparent 0%, rgba(0,0,0,0.4) 100%)'
        }}
      />
      
      {/* Subtle warm tint for premium feel */}
      <div className="absolute inset-0 bg-gradient-to-tr from-amber-900/10 via-transparent to-emerald-900/10" />
      
      {/* Top fade for header blend */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 to-transparent" />
      
      {/* Bottom fade for section blend */}
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/50 to-transparent" />
      
      {/* Subtle film grain texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />

      <div className="container relative mx-auto px-3 sm:px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6 md:space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-wellness" />
            <span className="text-xs sm:text-sm font-medium text-white">
              Provider-Guided Wellness Programs
            </span>
          </div>

          {/* Headline & Subheadline */}
          <div className="space-y-3 sm:space-y-4">
            <div className="min-h-[80px] sm:min-h-[90px] md:min-h-[120px] lg:min-h-[140px] flex flex-col items-center justify-center px-2 sm:px-2">
              <h1 
                className="text-[1.75rem] leading-tight sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-[#ffdb4d] sm:leading-snug text-center"
                style={{
                  textShadow: '0 2px 20px rgba(0,0,0,0.5), 0 4px 40px rgba(0,0,0,0.3)'
                }}
              >
                More medical weight loss, less orange-mansplaining.
              </h1>
            </div>
            <p 
              className="text-base sm:text-xl md:text-2xl text-white/90 font-medium max-w-3xl mx-auto text-center leading-relaxed px-2"
              style={{
                textShadow: '0 2px 10px rgba(0,0,0,0.5)'
              }}
            >
              Personalized GLP-1 programs, delivered to your doorstep.
            </p>
          </div>

          {/* CTAs - Side by side */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 pt-2 px-4 sm:px-2">
            <Button 
              size="lg" 
              className="text-sm sm:text-lg px-5 sm:px-8 py-3 sm:py-4 h-auto font-bold shadow-2xl hover:shadow-primary/25 transition-all group w-full sm:w-auto min-h-[48px] flex flex-col items-center gap-1"
              onClick={onOpenModal}
            >
              <span className="flex items-center">
                Weight Loss starting at $74/mo
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <span className="flex items-center gap-1.5 text-[10px] sm:text-xs font-normal opacity-80">
                powered by:
                <img 
                  src={orderlyMedsLogo} 
                  alt="OrderlyMeds" 
                  className="h-3 sm:h-4"
                />
              </span>
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="text-sm sm:text-lg px-5 sm:px-8 py-4 sm:py-6 h-auto font-semibold bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm w-full sm:w-auto shadow-xl min-h-[48px]"
              onClick={onOpenModal}
            >
              See if you qualify
            </Button>
          </div>

          {/* Trust Stats Grid */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 max-w-2xl mx-auto pt-2 sm:pt-4 px-2">
            {trustStats.map((stat, index) => (
              <AnimatedStat key={stat.label} stat={stat} delay={index * 150} />
            ))}
          </div>

          {/* LegitScript Badge */}
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            <img 
              src={legitScriptBadge} 
              alt="LegitScript Certified" 
              width={52}
              height={56}
              className="h-8 sm:h-10 md:h-12 opacity-90 hover:opacity-100 transition-opacity"
            />
            <span className="text-[10px] sm:text-xs text-white/50">Verified & Certified</span>
          </div>

          {/* Trust indicators */}
          <div className="pt-2 md:pt-4 flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:gap-8 text-white/80 px-2">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-wellness" />
              <span className="text-xs sm:text-sm font-medium">HIPAA Compliant</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-wellness" />
              <span className="text-xs sm:text-sm font-medium">Licensed Providers</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-wellness" />
              <span className="text-xs sm:text-sm font-medium">Telehealth Enabled</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrueWellnessHero;
